import { AxiosRequestConfig } from 'axios';
import { useMutation, UseMutationOptions } from 'react-query';
import { getPublicAxios } from './publicAxios';

export type PublicMutationFnParams = AxiosRequestConfig['data'] & {
  __axios?: AxiosRequestConfig;
};

/**
 * `useSubmit` for anonymous (`/public/...`) endpoints: same `(url, axiosCfg, mutationOptions)` shape and
 * same `__axios` per-call override key, but routed through the interceptor-free instance in `publicAxios`
 * so a rejected token surfaces as a plain mutation error instead of an Auth0 logout.
 *
 * `TPayload` defaults to the loose params `useSubmit` accepts, so this is a drop-in for it; a caller that
 * knows its contract can pin the payload instead and skip the `as any` cast at the call site.
 */
export const usePublicSubmit = <TResponse, TPayload = PublicMutationFnParams>(
  url: string,
  axiosCfg: AxiosRequestConfig = {},
  mutationOptions: UseMutationOptions<TResponse, Error, TPayload> = {}
) => {
  const mutation = useMutation<TResponse, Error, TPayload>({
    mutationFn: async (data: TPayload): Promise<TResponse> => {
      const { __axios, ...body } = (data ?? {}) as PublicMutationFnParams;
      const axiosRes = await getPublicAxios().request({
        url,
        method: 'POST',
        data: body,
        ...axiosCfg,
        ...__axios,
      });
      return axiosRes.data;
    },
    ...mutationOptions,
  });

  return mutation;
};
